import { useState, useEffect, useCallback, useRef } from "react";
import { useAppStore } from "../stores/appStore";
import { calculateNextExecutionTime } from "../utils/scheduledJobsHelper";

/**
 * 定时任务管理 Hook
 * 处理定时任务的创建、恢复和清理
 */
export const useScheduledJobs = (taskManager, addLog, { enabled = true } = {}) => {
  const [jobTimers, setJobTimers] = useState({});
  const timersRef = useRef({});
  const initializedRef = useRef(false);

  const cancelAllScheduledTimers = useCallback(() => {
    Object.values(timersRef.current).forEach(timer => clearTimeout(timer));
    timersRef.current = {};
    setJobTimers({});
    initializedRef.current = true;
  }, []);

  const {
    addScheduledJob,
    updateScheduledJob,
    removeScheduledJob,
    getAllScheduledJobs,
  } = useAppStore();

  // 执行定时任务的函数
  const executeScheduledJob = useCallback(
    async (jobId) => {
      if (!enabled || taskManager?.migrationPaused || taskManager?.transportPaused) return;
      // 直接从 store 获取最新的任务信息，避免闭包问题
      const { scheduledJobs: latestScheduledJobs } = useAppStore.getState();
      const currentJob = latestScheduledJobs[jobId];

      if (!currentJob) {
        addLog(`定时任务 ${jobId} 未找到`, "error");
        return;
      }

      addLog(`开始执行定时任务 ${jobId}`, "info");
      updateScheduledJob(jobId, { status: "executing" });

      try {
        const executionId = `scheduled_${jobId}_${Date.now()}`;

        taskManager?.addBatchToQueue({
          source: 'scheduled',
          scheduledJobId: jobId,
          tasks: currentJob.tasks,
          accounts: currentJob.accounts,
          configs: currentJob.configs,
          requestInterval: currentJob.requestInterval,
          executionId: executionId,
          onProgress: () => {},
          onLog: addLog,
        });

        addLog(
          `定时任务 ${jobId} 已加入执行队列，执行ID: ${executionId}`,
          "info"
        );

        // 计算下一次执行时间
        const nextExecution = calculateNextExecutionTime(
          currentJob.originalScheduleTime || "09:00",
          new Date()
        );

        // 设置下一次执行的定时器
        const nextTimerId = setTimeout(() => {
          executeScheduledJob(jobId);
        }, nextExecution.getTime() - Date.now());

        // 更新定时器引用
        timersRef.current[jobId] = nextTimerId;
        setJobTimers((prev) => ({
          ...prev,
          [jobId]: nextTimerId,
        }));

        // 更新任务状态
        updateScheduledJob(jobId, {
          status: "scheduled",
          scheduledTime: nextExecution.toISOString(),
          lastExecutedAt: new Date().toISOString(),
        });
      } catch (error) {
        addLog(`定时任务 ${jobId} 执行失败: ${error.message}`, "error");
        updateScheduledJob(jobId, { status: "failed" });
      }
    },
    [enabled, taskManager, addLog, updateScheduledJob]
  ); // 移除 scheduledJobs 依赖

  // 恢复定时任务
  const restoreScheduledJobs = useCallback(() => {
    if (!enabled || !taskManager || taskManager.transportPaused || !addLog || initializedRef.current) {
      return;
    }

    // 直接从 store 获取最新的任务信息
    const { scheduledJobs: latestScheduledJobs } = useAppStore.getState();
    const now = new Date();
    const newTimers = {};

    Object.values(latestScheduledJobs).forEach((job) => {
      if (job.status === "scheduled") {
        const scheduledTime = new Date(job.scheduledTime);

        if (scheduledTime > now) {
          const timeUntilExecution = scheduledTime.getTime() - now.getTime();
          const timerId = setTimeout(() => {
            executeScheduledJob(job.id);
          }, timeUntilExecution);

          newTimers[job.id] = timerId;
          addLog(
            `已恢复定时任务 ${
              job.id
            }，将在 ${scheduledTime.toLocaleString()} 执行`,
            "info"
          );
        } else {
          // 任务已过了预定执行时间，重新安排到下一个执行时间
          const nextExecution = calculateNextExecutionTime(
            job.originalScheduleTime || "09:00",
            now
          );

          const timeUntilNextExecution =
            nextExecution.getTime() - now.getTime();
          const timerId = setTimeout(() => {
            executeScheduledJob(job.id);
          }, timeUntilNextExecution);

          newTimers[job.id] = timerId;

          // 更新任务的下次执行时间
          updateScheduledJob(job.id, {
            scheduledTime: nextExecution.toISOString(),
          });

          addLog(
            `定时任务 ${
              job.id
            } 已错过执行时间，重新安排到 ${nextExecution.toLocaleString()}`,
            "warning"
          );
        }
      }
    });

    timersRef.current = newTimers;
    setJobTimers(newTimers);
    initializedRef.current = true;

    console.log(
      "定时任务恢复完成，共恢复",
      Object.keys(newTimers).length,
      "个定时器"
    );
  }, [enabled, taskManager, addLog, executeScheduledJob, updateScheduledJob]); // 移除 scheduledJobs 依赖

  const rescheduleScheduledJobs = useCallback(() => {
    if (!enabled || !taskManager || taskManager.transportPaused) return;

    Object.values(timersRef.current).forEach((timerId) => {
      if (timerId) clearTimeout(timerId);
    });

    const now = new Date();
    const nextTimers = {};
    const { scheduledJobs: latestScheduledJobs } = useAppStore.getState();
    Object.values(latestScheduledJobs).forEach((job) => {
      if (!job || job.status !== "scheduled") return;
      const nextExecution = calculateNextExecutionTime(
        job.originalScheduleTime || "09:00",
        now
      );
      const timerId = setTimeout(() => {
        executeScheduledJob(job.id);
      }, nextExecution.getTime() - Date.now());
      nextTimers[job.id] = timerId;
      updateScheduledJob(job.id, { scheduledTime: nextExecution.toISOString() });
    });

    timersRef.current = nextTimers;
    setJobTimers(nextTimers);
    initializedRef.current = true;
    addLog(`已按当天计划重排 ${Object.keys(nextTimers).length} 个定时任务`, "info");
  }, [enabled, taskManager, addLog, executeScheduledJob, updateScheduledJob]);

  // 创建新的定时任务
  const createScheduledJob = useCallback(
    (jobData, scheduleTime) => {
      if (!enabled || taskManager?.transportPaused) return null;
      const [hours, minutes] = scheduleTime.split(":").map(Number);
      const now = new Date();
      const scheduledDate = new Date();
      scheduledDate.setHours(hours, minutes, 0, 0);

      if (scheduledDate <= now) {
        scheduledDate.setDate(scheduledDate.getDate() + 1);
      }

      const completeJobData = {
        ...jobData,
        scheduledTime: scheduledDate.toISOString(),
        originalScheduleTime: scheduleTime,
      };

      const jobId = addScheduledJob(completeJobData);

      const timeUntilExecution = scheduledDate.getTime() - now.getTime();
      const timerId = setTimeout(() => {
        console.log(`定时器触发：执行定时任务 ${jobId}`);
        executeScheduledJob(jobId);
      }, timeUntilExecution);

      timersRef.current[jobId] = timerId;
      setJobTimers((prev) => ({
        ...prev,
        [jobId]: timerId,
      }));

      console.log(
        `创建定时任务 ${jobId}，定时器ID: ${timerId}，${Math.round(
          timeUntilExecution / 1000
        )}秒后执行`
      );
      addLog(
        `已安排定时任务 ${jobId}，将在 ${scheduledDate.toLocaleString()} 执行`,
        "info"
      );
      return jobId;
    },
    [enabled, taskManager, addScheduledJob, executeScheduledJob, addLog]
  );

  // 取消定时任务
  const cancelScheduledJob = useCallback(
    (jobId) => {
      if (!enabled) return;
      const timerId = timersRef.current[jobId];
      if (timerId) {
        clearTimeout(timerId);
        delete timersRef.current[jobId];
        setJobTimers((prev) => {
          const newTimers = { ...prev };
          delete newTimers[jobId];
          return newTimers;
        });
      }

      removeScheduledJob(jobId);
      addLog(`已取消定时任务 ${jobId}`, "info");
    },
    [enabled, removeScheduledJob, addLog]
  );

  // 初始化定时任务
  useEffect(() => {
    if (enabled && taskManager && addLog) {
      restoreScheduledJobs();
    }
  }, [enabled, taskManager, addLog, restoreScheduledJobs]); // 只依赖这两个参数

  useEffect(() => {
    if (enabled) return;
    Object.values(timersRef.current).forEach((timerId) => {
      if (timerId) clearTimeout(timerId);
    });
    timersRef.current = {};
    setJobTimers({});
    initializedRef.current = false;
  }, [enabled]);

  // 清理定时器
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((timerId) => {
        if (timerId) clearTimeout(timerId);
      });
      console.log("清理所有定时器");
    };
  }, []);

  return {
    cancelAllScheduledTimers,
    scheduledJobsList: enabled ? getAllScheduledJobs() : [],
    createScheduledJob,
    cancelScheduledJob,
    rescheduleScheduledJobs,
    jobTimers,
  };
};
